import type { DeliveryRow } from "@hermes-personal-assistant/shared";

export type DeliveryCardSummary = {
  item: string;
  reference: string | null;
  fallback: boolean;
};

export type DeliveryTimelineEntry = {
  key: string;
  status: string;
  at: string | null;
  expected_at: string | null;
  delivery_window: string | null;
  summary: string | null;
  details: string | null;
  source_message_id: string | null;
  gmail_message_id: string | null;
  source_url: string | null;
  isCurrent: boolean;
};

const TRACKING_LIKE_PATTERN = /\b(?:\d{3}-\d{7}-\d{7}|[A-Z0-9]{8,})\b/i;

export function deliveryCardSummary(delivery: DeliveryRow): DeliveryCardSummary {
  const item = deliveryShipmentItem(delivery);
  return {
    item: item ?? "Delivery item not identified yet",
    reference: deliveryReferenceLine(delivery),
    fallback: !item
  };
}

export function deliveryShipmentItem(delivery: DeliveryRow): string | null {
  const candidates = deliveryTextCandidates(delivery);
  for (const candidate of candidates) {
    const extracted = extractShipmentItem(candidate);
    if (extracted) {
      return extracted;
    }
  }
  return null;
}

export function deliveryReferenceLine(delivery: DeliveryRow): string | null {
  const parts: string[] = [];
  const courier = cleanText(delivery.courier);
  if (courier && !/^courier unknown$/i.test(courier)) {
    parts.push(courier);
  }
  const reference = cleanText(delivery.tracking_reference);
  if (reference) {
    const label = /^\d{3}-\d{7}-\d{7}$/.test(reference) ? "Order" : "Reference";
    parts.push(`${label} ${reference}`);
  }
  return parts.length > 0 ? parts.join(" • ") : null;
}

export function deliveryTimeline(delivery: DeliveryRow): DeliveryTimelineEntry[] {
  const entries: DeliveryTimelineEntry[] = (delivery.status_history ?? []).map((event, index) => ({
    key: `history-${event.source_message_id ?? event.gmail_message_id ?? event.at}-${index}`,
    status: event.status ?? delivery.status,
    at: event.at,
    expected_at: event.expected_at ?? null,
    delivery_window: event.delivery_window ?? null,
    summary: event.summary ?? null,
    details: event.details ?? null,
    source_message_id: event.source_message_id ?? null,
    gmail_message_id: event.gmail_message_id ?? null,
    source_url: event.source_url ?? null,
    isCurrent: false
  }));

  const currentAt = delivery.last_update_at ?? delivery.updated_at ?? delivery.created_at ?? null;
  const currentKey = `current-${delivery.status}-${currentAt ?? delivery.id}`;
  const hasSameCurrentEvent = entries.some((entry) => entry.status === delivery.status && sameTimestamp(entry.at, currentAt));
  if (!hasSameCurrentEvent) {
    entries.push({
      key: currentKey,
      status: delivery.status,
      at: currentAt,
      expected_at: delivery.expected_at ?? null,
      delivery_window: delivery.delivery_window ?? null,
      summary: delivery.relevance_summary ?? null,
      details: delivery.details ?? null,
      source_message_id: delivery.source_message_id ?? null,
      gmail_message_id: delivery.gmail_message_id ?? null,
      source_url: delivery.source_url ?? null,
      isCurrent: true
    });
  }

  entries.sort((left, right) => {
    const leftTime = left.at ? Date.parse(left.at) : 0;
    const rightTime = right.at ? Date.parse(right.at) : 0;
    return leftTime - rightTime;
  });

  return entries.map((entry) => ({
    ...entry,
    isCurrent: entry.isCurrent || (entry.status === delivery.status && sameTimestamp(entry.at, currentAt))
  }));
}

function deliveryTextCandidates(delivery: DeliveryRow): string[] {
  const history = [...(delivery.status_history ?? [])].reverse();
  const values = [
    delivery.details,
    delivery.relevance_summary,
    ...history.flatMap((event) => [event.details, event.summary])
  ];
  return values.filter((value): value is string => Boolean(cleanText(value)));
}

function extractShipmentItem(text: string): string | null {
  const cleaned = text.replace(/\s+/g, " ").trim();
  const patterns: RegExp[] = [
    /(?:shipment|package|parcel|delivery)\s+contains\s+(?:one\s+|an?\s+|\d+\s+)?(.+?)(?:,\s*with\b|,\s*via\b|,\s*tracking\b|;|\.|$)/i,
    /(?:says\s+)?(?:an?\s+)?order\s+for\s+(.+?)(?:\s+has\s+been|\s+is\s+currently|\s+is\s+expected|\s+expected\b|,\s*quantity|,\s*total|\s+costing\b|;|\.|$)/i,
    /order\s+confirmation\s+(?:and\s+[\w\s]+?\s+)?(?:from\s+.+?\s+)?for\s+order\s+[A-Z0-9-]+:\s*(?:\d+\s+|one\s+|an?\s+)?(.+?)(?:,\s*quantity|,\s*total|\s+is\s+currently|\s+is\s+expected|;|\.|$)/i,
    /order\s+confirmation\s+(?:and\s+\w+\s+)?(?:for\s+)?(?:order\s+[A-Z0-9-]+:\s*)?(?:\d+\s+|one\s+|an?\s+)?(.+?)(?:,\s*quantity|,\s*total|\s+is\s+currently|\s+is\s+expected|;|\.|$)/i,
    /package\s+on\s+the\s+way:\s*(.+?)(?:,|;|\.|$)/i,
    /Email\s+subject:\s*(?:Dispatched|Ordered|Shipped|Delivered|Out\s+for\s+delivery):\s*[‘'\"](.+?)[’'\"]/i
  ];
  for (const pattern of patterns) {
    const match = cleaned.match(pattern);
    const normalized = normalizeShipmentItem(match?.[1]);
    if (normalized) {
      return normalized;
    }
  }
  return null;
}

function normalizeShipmentItem(value: string | undefined): string | null {
  const cleaned = cleanText(value)
    ?.replace(/^['‘“\"]+|['’”\"]+$/g, "")
    .replace(/\s*\.\.\.$/, "")
    .replace(/\s+with\s+order\s+number\b.*$/i, "")
    .replace(/\s+order\s+number\b.*$/i, "")
    .replace(/\s+total\s+£?[\d,.]+.*$/i, "")
    .replace(/\s+costing\s+£?[\d,.]+.*$/i, "")
    .trim();
  if (!cleaned || cleaned.length < 3) {
    return null;
  }
  if (TRACKING_LIKE_PATTERN.test(cleaned) && cleaned.replace(TRACKING_LIKE_PATTERN, "").trim().length < 3) {
    return null;
  }
  if (/^(order|package|delivery|shipment|reference)$/i.test(cleaned)) {
    return null;
  }
  return cleaned;
}

function cleanText(value: string | null | undefined): string | null {
  const cleaned = value?.replace(/\s+/g, " ").trim();
  return cleaned || null;
}

function sameTimestamp(left: string | null, right: string | null): boolean {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return !left && !right;
  }
  const leftTime = Date.parse(left);
  const rightTime = Date.parse(right);
  if (!Number.isFinite(leftTime) || !Number.isFinite(rightTime)) {
    return false;
  }
  return leftTime === rightTime;
}
